import React, { useState } from 'react';
import {
  CreditCard, ChevronDown, Mail, MapPin, Calendar, Building,
  Eye, EyeOff, ShieldCheck, Users, Pencil, Check, X, Loader, Copy, Plus
} from 'lucide-react';
import CreateSocialAccountModal from '../Talent/CreateSocialAccountModal';
import EditSocialAccountModal from '../Talent/EditSocialAccountModal';
import { DatePicker } from '@anyemp/ui-kit';

/**
 * Переиспользуемый accordion-элемент для отображения аккаунта.
 * Рендерит как accordion-обёртку, так и всё тело аккаунта с условными секциями.
 *
 * Props — accordion:
 *   account         - объект аккаунта
 *   accountKey      - уникальный ключ (обычно account.id || index)
 *   isExpanded      - открыт ли элемент
 *   onToggle        - fn(accountKey) — вызывается при клике на header
 *   index           - порядковый индекс (для animation-delay)
 *   badges          - ReactNode — дополнительные бейджи справа в header
 *   headerActions   - ReactNode — кнопки действий (Edit и т.п.) справа от chevron
 *   maxHeight       - строка CSS max-height для expanded state (default: '3000px')
 *
 * Props — управление паролями:
 *   visiblePasswords       - объект { key: bool } для хранения видимости паролей
 *   onTogglePassword       - fn(id, type) — переключение видимости пароля
 *   canViewPassword        - bool — показывать ли поле пароля
 *
 * Props — SubAi (только в AccountTab):
 *   canEditSubAiLimitDate  - bool
 *   editingSubAiLimitDate  - id редактируемого subAi или null
 *   limitDateEditValue     - string — текущее значение input
 *   onChangeLimitDate      - fn(value) — изменение значения
 *   onEditLimitDate        - fn(subAi) — начать редактирование
 *   onSaveLimitDate        - fn(subAiId) — сохранить
 *   onCancelLimitDate      - fn() — отмена
 *   savingSubAiLimitDate   - id сохраняемого subAi или null
 *
 * Props — форматирование:
 *   formatDate             - fn(dateString) — форматирование даты (передаётся снаружи или используется внутренний)
 */

const defaultFormatDate = (dateString) => {
  if (!dateString) return 'Not specified';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return 'Invalid date';
  }
};

const AccountAccordionItem = ({
  account,
  accountKey,
  isExpanded,
  onToggle,
  index = 0,
  badges,
  headerActions,
  maxHeight = '3000px',
  // Password
  visiblePasswords = {},
  onTogglePassword,
  canViewPassword = true,
  // SubAi editing
  canEditSubAiLimitDate = false,
  editingSubAiLimitDate = null,
  limitDateEditValue = '',
  onChangeLimitDate,
  onEditLimitDate,
  onSaveLimitDate,
  onCancelLimitDate,
  savingSubAiLimitDate = null,
  // Date formatter
  formatDate = defaultFormatDate,
  // Extra content slot (e.g. Notifications, Notes for social accounts)
  extraContent = null,
  // Owner mode — shows "Add Social Account" button
  isOwner = false,
  // Visibility flags
  showSecurity = true,
  showTimeline = true,
}) => {
  const [showCreateSocialModal, setShowCreateSocialModal] = useState(false);
  const [editingSocialAccount, setEditingSocialAccount] = useState(null);

  // Внутренние пароли, если onTogglePassword не передан
  const [internalVisible, setInternalVisible] = useState({});
  const effectiveVisible = onTogglePassword ? visiblePasswords : internalVisible;
  const effectiveToggle = onTogglePassword || ((id, type) => {
    const key = `${id}-${type}`;
    setInternalVisible(prev => ({ ...prev, [key]: !prev[key] }));
  });

  const [copiedKeys, setCopiedKeys] = useState({});
  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKeys(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopiedKeys(prev => ({ ...prev, [key]: false })), 2000);
    });
  };

  const hasSocial = account.socialAccounts && account.socialAccounts.length > 0;
  const hasAssignments = account.userAssignments && account.userAssignments.length > 0;
  const hasSubAi = Array.isArray(account.subAi) && account.subAi.length > 0;
  const hasSecurity = account.two_factor_enabled !== undefined || account.is_hidden_from_employee !== undefined;
  const hasTimeline = account.created_at || account.updated_at;

  return (
    <>
    <div
      className="relative bg-gradient-to-br from-light-bg-primary to-light-bg-secondary dark:from-dark-bg-tertiary dark:to-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 animate-slide-up overflow-hidden"
      style={{ animationDelay: `${index * 30}ms` }}
    >
      {/* Декоративный градиент сверху */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500" />

      {/* Header row */}
      <div className="flex w-full flex-col sm:flex-row sm:items-center">
        {/* Кликабельная область accordion */}
        <button
          onClick={() => onToggle(accountKey)}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-light-bg-tertiary/40 dark:hover:bg-dark-bg-primary/40 transition-colors duration-200 sm:flex-nowrap sm:px-5 sm:py-4"
        >
          {/* Иконка тула */}
          {account.tool?.icon ? (
            <img
              src={account.tool.icon}
              alt={account.tool.name || 'Tool icon'}
              className="w-9 h-9 rounded-lg shadow-sm ring-1 ring-light-border-primary/50 dark:ring-dark-border-primary/50 flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 flex items-center justify-center flex-shrink-0">
              <CreditCard className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            </div>
          )}

          {/* Название и subtitle */}
          <div className="flex-1 min-w-0">
            <div className="break-words text-sm font-bold text-light-text-primary dark:text-dark-text-primary sm:truncate">
              {account.name || account.login || account.tool?.name || 'Account'}
            </div>
            {account.name && account.login && (
              <div className="mt-0.5 break-all text-xs text-light-text-tertiary dark:text-dark-text-tertiary sm:truncate">
                {account.login}
              </div>
            )}
          </div>

          {/* Бейджи (статус + переданные извне) */}
          <div className="order-3 ml-12 flex min-w-0 w-[calc(100%_-_3rem)] flex-wrap items-center gap-2 sm:order-none sm:ml-0 sm:w-auto sm:flex-shrink-0 sm:flex-nowrap">
            {badges}
            {account.status && (
              <span
                className="inline-flex px-3 py-1 text-xs font-bold rounded-full"
                style={{
                  backgroundColor: account.status.color ? `${account.status.color}15` : undefined,
                  color: account.status.color || undefined,
                  border: account.status.color ? `1.5px solid ${account.status.color}50` : undefined
                }}
              >
                {account.status.name}
              </span>
            )}
          </div>

          {/* Chevron */}
          <ChevronDown
            className={`h-5 w-5 ml-2 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Действия вне кнопки accordion (Edit, Delete и т.п.) */}
        {headerActions && (
          <div className="flex flex-shrink-0 justify-end px-4 pb-3 [&_button]:min-h-10 [&_button]:min-w-10 sm:px-0 sm:pb-0 sm:pr-4">
            {headerActions}
          </div>
        )}
      </div>

      {/* Expandable body */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isExpanded ? maxHeight : '0px',
          opacity: isExpanded ? 1 : 0
        }}
      >
        <div className="px-5 pb-5 pt-2 border-t border-light-border-primary/30 dark:border-dark-border-primary/30">

          {/* ─── Basic fields: Login, Password, Tool Type, Link ─── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {account.login && (
              <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-4 border border-light-border-primary/50 dark:border-dark-border-primary/50 hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
                <label className="flex items-center text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary mb-2 uppercase tracking-wide">
                  <Mail className="h-3 w-3 mr-1.5" />
                  Login
                </label>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary break-all">
                  {account.login}
                </p>
              </div>
            )}

            {canViewPassword && account.password && (
              <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-4 border border-light-border-primary/50 dark:border-dark-border-primary/50 hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
                <label className="flex items-center text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary mb-2 uppercase tracking-wide">
                  Password
                </label>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <p className="min-w-0 flex-1 break-all font-mono text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                    {effectiveVisible[`${account.id}-account`] ? account.password : '••••••••'}
                  </p>
                  <button
                    onClick={() => effectiveToggle(account.id, 'account')}
                    aria-label={effectiveVisible[`${account.id}-account`] ? 'Hide password' : 'Show password'}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-600 transition-all duration-200 hover:bg-primary-100 hover:text-primary-700 dark:text-primary-400 dark:hover:bg-primary-900/30 dark:hover:text-primary-300"
                    title={effectiveVisible[`${account.id}-account`] ? 'Hide password' : 'Show password'}
                  >
                    {effectiveVisible[`${account.id}-account`] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleCopy(account.password, `${account.id}-account`)}
                    aria-label="Copy password"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-600 transition-all duration-200 hover:bg-primary-100 hover:text-primary-700 dark:text-primary-400 dark:hover:bg-primary-900/30 dark:hover:text-primary-300"
                    title="Copy password"
                  >
                    {copiedKeys[`${account.id}-account`] ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}

            {account.link && (
              <div className="md:col-span-2 bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-4 border border-light-border-primary/50 dark:border-dark-border-primary/50 hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
                <label className="flex items-center text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary mb-2 uppercase tracking-wide">
                  <MapPin className="h-3 w-3 mr-1.5" />
                  Link
                </label>
                <a
                  href={account.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline underline-offset-2 break-all"
                >
                  {account.link}
                </a>
              </div>
            )}
          </div>

          {/* ─── Security (2FA, Hidden from Employees) ─── */}
          {showSecurity && hasSecurity && (
            <div className="mb-5 pt-4 border-t border-light-border-primary/50 dark:border-dark-border-primary/50">
              <h5 className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center">
                <div className="p-1.5 bg-gradient-to-br from-green-500 to-green-600 rounded-lg mr-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-white" />
                </div>
                Security
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-4 border border-light-border-primary/50 dark:border-dark-border-primary/50 flex items-center justify-between">
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    2FA Enabled
                  </span>
                  <span className={`text-sm font-semibold ${account.two_factor_enabled ? 'text-green-600 dark:text-green-400' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}`}>
                    {account.two_factor_enabled ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-4 border border-light-border-primary/50 dark:border-dark-border-primary/50 flex items-center justify-between">
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary flex items-center gap-2">
                    <EyeOff className="h-4 w-4" />
                    Hidden from Employees
                  </span>
                  <span className={`text-sm font-semibold ${account.is_hidden_from_employee ? 'text-orange-600 dark:text-orange-400' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}`}>
                    {account.is_hidden_from_employee ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ─── SubAi ─── */}
          {hasSubAi && (
            <div className="mb-5 pt-5 border-t border-light-border-primary/50 dark:border-dark-border-primary/50">
              <h5 className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary mb-4 flex items-center">
                <div className="p-1.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-2">
                  <CreditCard className="h-3.5 w-3.5 text-white" />
                </div>
                SubAi
                <span className="ml-2 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold rounded-full">
                  {account.subAi.length}
                </span>
              </h5>
              <div className="space-y-4">
                {account.subAi.map((subAi, subAiIdx) => (
                  <div
                    key={subAi.id || subAiIdx}
                    className="bg-gradient-to-br from-light-bg-secondary to-light-bg-tertiary dark:from-dark-bg-primary dark:to-dark-bg-secondary rounded-xl p-5 border border-light-border-primary/50 dark:border-dark-border-primary/50"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary truncate">
                          {subAi.tool?.name || `Tool #${subAi.tool_id}`}
                        </div>
                        {subAi.external_id && (
                          <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
                            External ID: {subAi.external_id}
                          </div>
                        )}
                      </div>
                      {(subAi.status || subAi.status_id) && (
                        <span
                          className="inline-flex px-2.5 py-1 text-xs font-bold rounded-lg shadow-sm whitespace-nowrap"
                          style={{
                            backgroundColor: subAi.status?.color ? `${subAi.status.color}20` : undefined,
                            color: subAi.status?.color || undefined,
                            border: subAi.status?.color ? `1px solid ${subAi.status.color}60` : undefined
                          }}
                        >
                          {subAi.status?.name || `Status ${subAi.status_id}`}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary mb-1 block uppercase tracking-wide">
                          Limit Date
                        </label>
                        {editingSubAiLimitDate === subAi.id ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <DatePicker
                              size="small"
                              showTime
                              format="DD.MM.YYYY HH:mm"
                              value={limitDateEditValue ? new Date(limitDateEditValue) : null}
                              onChange={(d) => onChangeLimitDate?.(d ? d.toISOString() : '')}
                            />
                            <button
                              onClick={() => onSaveLimitDate?.(subAi.id)}
                              disabled={savingSubAiLimitDate === subAi.id}
                              aria-label="Save limit date"
                              className="inline-flex h-10 w-10 items-center justify-center rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
                              title="Save"
                            >
                              {savingSubAiLimitDate === subAi.id ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => onCancelLimitDate?.()}
                              disabled={savingSubAiLimitDate === subAi.id}
                              aria-label="Cancel limit date editing"
                              className="inline-flex h-10 w-10 items-center justify-center rounded bg-light-bg-tertiary text-light-text-primary hover:bg-light-border-primary dark:bg-dark-bg-tertiary dark:text-dark-text-primary dark:hover:bg-dark-border-primary"
                              title="Cancel"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-light-text-primary dark:text-dark-text-primary font-medium">
                              {subAi.limit_date ? formatDate(subAi.limit_date) : 'Not set'}
                            </span>
                            {canEditSubAiLimitDate && (
                              <button
                                onClick={() => onEditLimitDate?.(subAi)}
                                aria-label="Edit limit date"
                                className="inline-flex h-10 w-10 items-center justify-center rounded text-primary-500 hover:bg-light-bg-tertiary dark:text-primary-400 dark:hover:bg-dark-bg-tertiary"
                                title="Edit limit date"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary mb-1 block uppercase tracking-wide">
                          Subscriptions
                        </label>
                        {(subAi.subscriptions || []).length > 0 ? (
                          <div className="space-y-1">
                            {subAi.subscriptions.map((subscription, subIdx) => (
                              <div key={subscription.id || subIdx} className="text-light-text-primary dark:text-dark-text-primary">
                                {subscription.status?.name || `Status ${subscription.status_id}`}
                                {subscription.expires_at && (
                                  <span className="text-light-text-tertiary dark:text-dark-text-tertiary">
                                    {' '}• exp {formatDate(subscription.expires_at)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-light-text-tertiary dark:text-dark-text-tertiary">No subscriptions</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Social Accounts ─── */}
          {(hasSocial || isOwner) && (
            <div className="mb-5 pt-4 border-t border-light-border-primary/50 dark:border-dark-border-primary/50">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
                  <div className="p-1.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-2">
                    <CreditCard className="h-3.5 w-3.5 text-white" />
                  </div>
                  Social Accounts
                  <span className="ml-2 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold rounded-full">
                    {account.socialAccounts?.length || 0}
                  </span>
                </h5>
                {isOwner && (
                  <button
                    onClick={() => setShowCreateSocialModal(true)}
                    className="inline-flex min-h-10 items-center gap-1 rounded-md px-3 py-2 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                  >
                    <Plus className="h-3 w-3" />
                    Add Social Account
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {account.socialAccounts.map((social) => (
                  <div
                    key={social.id}
                    className="bg-gradient-to-br from-light-bg-secondary to-light-bg-tertiary dark:from-dark-bg-primary dark:to-dark-bg-secondary rounded-xl p-4 border border-light-border-primary/50 dark:border-dark-border-primary/50 space-y-3"
                  >
                    {isOwner && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => setEditingSocialAccount(social)}
                          className="inline-flex min-h-10 items-center gap-1 rounded-md px-3 py-2 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                      </div>
                    )}
                    {social.username && (
                      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                        <span className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">Username</span>
                        <span className="min-w-0 break-all text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{social.username}</span>
                      </div>
                    )}
                    {social.login && (
                      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                        <span className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">Login</span>
                        <span className="min-w-0 break-all text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{social.login}</span>
                      </div>
                    )}
                    {social.password && (
                      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                        <span className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">Password</span>
                        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
                          <span className="min-w-0 break-all font-mono text-sm text-light-text-primary dark:text-dark-text-primary">
                            {effectiveVisible[`${social.id}-social`] ? social.password : '••••••••'}
                          </span>
                          <button
                            onClick={() => effectiveToggle(social.id, 'social')}
                            aria-label={effectiveVisible[`${social.id}-social`] ? 'Hide social account password' : 'Show social account password'}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-600 transition-all duration-200 hover:bg-primary-100 dark:text-primary-400 dark:hover:bg-primary-900/30"
                          >
                            {effectiveVisible[`${social.id}-social`] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            onClick={() => handleCopy(social.password, `${social.id}-social`)}
                            aria-label="Copy social account password"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-600 transition-all duration-200 hover:bg-primary-100 dark:text-primary-400 dark:hover:bg-primary-900/30"
                            title="Copy password"
                          >
                            {copiedKeys[`${social.id}-social`] ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </div>
                    )}
                    {social.link && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wide">Link</span>
                        <a
                          href={social.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline underline-offset-2"
                        >
                          Open Link
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── User Assignments ─── */}
          {hasAssignments && (
            <div className="mb-5 pt-4 border-t border-light-border-primary/50 dark:border-dark-border-primary/50">
              <h5 className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center">
                <div className="p-1.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mr-2">
                  <Users className="h-3.5 w-3.5 text-white" />
                </div>
                Assigned Users
                <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-full">
                  {account.userAssignments.length}
                </span>
              </h5>
              <div className="flex flex-wrap gap-2">
                {account.userAssignments.map((assignment) => (
                  <span
                    key={assignment.id}
                    className="px-3 py-1.5 bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 text-primary-700 dark:text-primary-300 rounded-lg text-xs font-semibold border border-primary-200 dark:border-primary-700/50"
                  >
                    {assignment.user?.name || assignment.user_id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ─── Timeline ─── */}
          {showTimeline && hasTimeline && (
            <div className="pt-4 border-t border-light-border-primary/50 dark:border-dark-border-primary/50">
              {account.created_at && account.updated_at ? (
                <>
                  <h5 className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary mb-4 flex items-center">
                    <div className="p-1.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-2">
                      <Calendar className="h-3.5 w-3.5 text-white" />
                    </div>
                    Timeline
                  </h5>
                  <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-xl p-4 space-y-3 border border-light-border-primary/50 dark:border-dark-border-primary/50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-light-text-secondary dark:text-dark-text-secondary">Created:</span>
                      <span className="text-sm text-light-text-primary dark:text-dark-text-primary font-medium">{formatDate(account.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-light-text-secondary dark:text-dark-text-secondary">Updated:</span>
                      <span className="text-sm text-light-text-primary dark:text-dark-text-primary font-medium">{formatDate(account.updated_at)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Created: {formatDate(account.created_at || account.updated_at)}</span>
                </div>
              )}
            </div>
          )}

          {/* ─── Extra content slot (e.g. Notifications, Notes) ─── */}
          {extraContent && (
            <div className="pt-4 border-t border-light-border-primary/50 dark:border-dark-border-primary/50">
              {extraContent}
            </div>
          )}

        </div>
      </div>
    </div>

    {showCreateSocialModal && (
      <CreateSocialAccountModal
        isOpen={showCreateSocialModal}
        onClose={() => setShowCreateSocialModal(false)}
        accountId={account.id}
      />
    )}
    {editingSocialAccount && (
      <EditSocialAccountModal
        isOpen={!!editingSocialAccount}
        onClose={() => setEditingSocialAccount(null)}
        socialAccount={editingSocialAccount}
      />
    )}
    </>
  );
};

export default AccountAccordionItem;
